import { useEffect } from "react";

import {
  useGetNotePredictionUserSettings,
  usePatchNotePredictionUserSettings,
} from "@elation/api-client/src/note-predictions-api";
import {
  isNablaAPILanguage,
  isRecordingActiveStep,
  NABLA_API_LANGUAGE_OPTIONS,
  type ScribeLanguage,
  useScribeLanguage,
  useStep,
} from "@elation/transcribe";

import { usePracticeId } from "@src/features/practices";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/shared/components/ui/select";
import { Spinner } from "@src/shared/components/ui/spinner";

interface ScribeLanguageSelectProps {
  disabled?: boolean;
  onChange?: (value: ScribeLanguage) => void;
}

/**
 * Select component to pick the Nabla transcription language for new scribe sessions.
 * Persists the choice to the user's note-prediction settings on change.
 */
export function ScribeLanguageSelect({
  disabled,
  onChange,
}: ScribeLanguageSelectProps) {
  const practiceId = usePracticeId();
  const [step] = useStep();
  const [language, setLanguage] = useScribeLanguage();
  const isLocked = isRecordingActiveStep(step) || step === "finishing";

  const { data: userSettings, isFetching: isLoadingUserSettings } =
    useGetNotePredictionUserSettings(practiceId);

  const { mutate: patchUserSettings } = usePatchNotePredictionUserSettings();

  const handleChange = (value: string): void => {
    const nextLanguage = value as ScribeLanguage;

    setLanguage(nextLanguage);

    patchUserSettings({
      practiceId,
      data: { language: nextLanguage },
    });

    onChange?.(nextLanguage);
  };

  useEffect(() => {
    const savedLanguage = userSettings?.data.language;

    if (isNablaAPILanguage(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, [setLanguage, userSettings?.data.language]);

  return (
    <div className="space-y-2">
      <Select
        value={language}
        onValueChange={handleChange}
        disabled={disabled || isLocked || isLoadingUserSettings}
        name="scribeLanguage"
      >
        <SelectTrigger
          aria-label="Transcription language"
          data-dd-privacy="allow"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <SelectValue placeholder="English" />
            {isLoadingUserSettings ? (
              <Spinner
                size="sm"
                aria-label="Loading language settings"
                className="shrink-0"
              />
            ) : null}
          </div>
        </SelectTrigger>
        <SelectContent>
          {NABLA_API_LANGUAGE_OPTIONS.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs leading-snug text-muted-foreground">
        Transcription will always support English in addition to the selected
        language.
      </p>
    </div>
  );
}
